import { useState, useCallback, useEffect, useRef } from "react";
import { getTodayDate } from "../../utils/constants";
import useApi from "../../hooks/useApi";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";

import AutoCompleteCommon from "./AutoCompleteCommon";

export default function AssetValuationTabs(props) {
  const {
    generateDepreciationSchedule,
    depreciationList,
    setDepreciationList,
    setSelectedDepreciationAccount,
    selectedDepreciationAccount,
    setSelectedAccumulatedDepreciationAccount,
    selectedAccumulatedDepreciationAccount,
    setSelectedAssetAccount,
    selectedAssetAccount,
    setDepreciationObject,
    depreciationObject,
  } = props;
  const [activeTab, setActiveTab] = useState("valuation");

    const COMPANY_CODE = localStorage?.getItem("companyCode") || "";


  return (
    <div className="mt-8">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <Tab
            label="Asset Valuation"
            active={activeTab === "valuation"}
            onClick={() => setActiveTab("valuation")}
          />
          <Tab
            label="Depreciation Schedule"
            active={activeTab === "depreciation"}
            onClick={() => setActiveTab("depreciation")}
          />
        </nav>
      </div>

      <div className="mt-6">
        {/* Valuation Tab - Always in DOM, hidden if not active */}
        <div className={activeTab === "valuation" ? "block" : "hidden"}>
          <ValuationTab
            generateDepreciationSchedule={generateDepreciationSchedule}
            setDepreciationList={setDepreciationList}
            setSelectedDepreciationAccount={setSelectedDepreciationAccount}
            selectedDepreciationAccount={selectedDepreciationAccount}
            setSelectedAccumulatedDepreciationAccount={
              setSelectedAccumulatedDepreciationAccount
            }
            selectedAccumulatedDepreciationAccount={
              selectedAccumulatedDepreciationAccount
            }
            setSelectedAssetAccount={setSelectedAssetAccount}
            selectedAssetAccount={selectedAssetAccount}
            setDepreciationObject={setDepreciationObject}
            depreciationObject={depreciationObject}
          />
        </div>

        {/* Depreciation Tab - Always in DOM, hidden if not active */}
        <div className={activeTab === "depreciation" ? "block" : "hidden"}>
          <DepreciationTab depreciationList={depreciationList} />
        </div>
      </div>
    </div>
  );
}

/* ---------- TAB BUTTON ---------- */
function Tab({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-b-2 px-1 pb-3 text-sm font-medium
        ${
          active
            ? "border-indigo-600 text-indigo-600"
            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
        }`}
    >
      {label}
    </button>
  );
}

/* ---------- REUSABLE INPUT ---------- */
function Input({
  label,
  type = "text",
  name,
  value,
  onChange,
  min,
  max,
  disabled = false,
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        name={name}
        onChange={onChange}
        value={value}
        type={type}
        min={type === "number" ? min : undefined}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
        disabled={disabled}
        max={type === "date" ? max : undefined}
        onWheel={(e) => e.target.blur()}
      />
    </div>
  );
}

/* ---------- TAB 1 : ASSET VALUATION ---------- */
function ValuationTab(props) {
  const {
    generateDepreciationSchedule,
    setDepreciationList,
    setSelectedDepreciationAccount,
    selectedDepreciationAccount,
    setSelectedAccumulatedDepreciationAccount,
    selectedAccumulatedDepreciationAccount,
    setSelectedAssetAccount,
    selectedAssetAccount,
    setDepreciationObject,
    depreciationObject,
  } = props;

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    console.log("value", value);

    // Prevent direct editing of calculated field
    if (name === "opening_book_value") {
      return;
    }

    // Parse numeric values
    const numValue = value === "" ? "" : parseFloat(value);

    // Validate salvage value
    if (name === "estimated_salvage_value") {
      const purchaseValue = parseFloat(depreciationObject.purchase_value) || 0;
      if (numValue > purchaseValue) {
        return;
      }
    }

    if (name === "purchase_value") {
      setDepreciationObject((prev) => ({
        ...prev,
        [name]: value,
        estimated_salvage_value: "0",
        periodic_depreciation_amount: "0",
      }));
    } else {
      setDepreciationObject((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  useEffect(() => {
    const purchaseValue = parseFloat(depreciationObject.purchase_value) || 0;
    const salvageValue =
      parseFloat(depreciationObject.estimated_salvage_value) || 0;

    if (purchaseValue && salvageValue <= purchaseValue) {
      setDepreciationObject((prev) => ({
        ...prev,
        opening_book_value: (purchaseValue - salvageValue).toString(),
      }));
    } else {
      setDepreciationObject((prev) => ({
        ...prev,
        opening_book_value: "0",
      }));
    }
  }, [
    depreciationObject.purchase_value,
    depreciationObject.estimated_salvage_value,
  ]);

  useEffect(() => {
    const depreciationMonths = parseFloat(
      depreciationObject.depreciation_months
    );

    console.log("depreciationMonths", depreciationMonths);
    if (!depreciationMonths || depreciationMonths <= 0) {
      setDepreciationObject((prev) => ({
        ...prev,
        periodic_depreciation_amount: "0",
      }));
    }
  }, [depreciationObject.depreciation_months]);

  useEffect(() => {
    const opening_book_value =
      parseFloat(depreciationObject.opening_book_value) || 0;
    const depreciation_months =
      parseFloat(depreciationObject.depreciation_months) || 0;

    if (depreciation_months === 0) {
      return;
    }

    if (opening_book_value && depreciation_months) {
      setDepreciationObject((prev) => ({
        ...prev,
        periodic_depreciation_amount: (opening_book_value / depreciation_months)
          .toFixed(2)
          .toString(),
      }));
    } else {
      setDepreciationObject((prev) => ({
        ...prev,
        periodic_depreciation_amount: "0",
      }));
    }
  }, [
    depreciationObject.opening_book_value,
    depreciationObject.depreciation_months,
  ]);

  useEffect(() => {
    setDepreciationList(null);
  }, [
    depreciationObject.purchase_value,
    depreciationObject.opening_book_value,
    depreciationObject.depreciation_months,
  ]);

  useEffect(() => {
    if (
      depreciationObject.start_date &&
      depreciationObject.depreciation_months != 0 &&
      depreciationObject.opening_book_value != 0
    ) {
      generateDepreciationSchedule(depreciationObject);
    }
  }, [
    depreciationObject.start_date,
    depreciationObject.depreciation_months,
    depreciationObject.opening_book_value,
  ]);

  const checkIfValidObject = () => {
    // const periodicAmount = parseFloat(
    //   depreciationObject.periodic_depreciation_amount
    // );
    // if (!periodicAmount || periodicAmount <= 0) {
    //   alert("Periodic depreciation amount not set");
    //   return false;
    // }
    // if (depreciationObject.start_date?.trim() === "") {
    //   alert("Enter valid start date");
    //   return;
    // }
    // const depreciationMonths = parseFloat(
    //   depreciationObject.depreciation_months
    // );
    // if (!depreciationMonths || depreciationMonths <= 0) {
    //   alert("Enter valid depreciation months");
    //   return;
    // }
    // if (!selectedDepreciationAccount) {
    //   alert("Enter Depreciation Account");
    //   return;
    // }
    // if (!selectedAccumulatedDepreciationAccount) {
    //   alert("Enter Accumulated Depreciation Account");
    //   return;
    // }
    // if (!selectedAssetAccount) {
    //   alert("Enter Asset Account");
    //   return;
    // }
    //generateDepreciationSchedule(depreciationObject);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      {/* <button
        type="button"
        onClick={() => checkIfValidObject(depreciationObject)}
        className="flex items-center gap-2 px-8 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 active:scale-95 rounded-xl shadow-lg shadow-indigo-200 transition-all disabled:opacity-70 disabled:pointer-events-none"
      >
        Generate Schedule
      </button> */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
        <Input
          type="date"
          label="Purchase Date"
          name="purchase_date"
          value={depreciationObject.purchase_date}
          onChange={handleInputChange}
          max={getTodayDate()}
        />

        <Input
          type="date"
          label="Start Date * "
          name="start_date"
          value={depreciationObject.start_date}
          onChange={handleInputChange} // Always returns "YYYY-MM-DD" also for setting ALWAYS use YYYY-MM-DD format in different browser may show in ui in different format
        />
        <Input
          type="number"
          label="Purchase Value * "
          name="purchase_value"
          value={depreciationObject.purchase_value}
          onChange={handleInputChange}
        />
        <Input
          min={0}
          type="number"
          label="Estimated Salvage Value"
          name="estimated_salvage_value"
          value={depreciationObject.estimated_salvage_value}
          onChange={handleInputChange}
          max={depreciationObject.purchase_value || undefined} // cannot exceed purchase
          disabled={!depreciationObject.purchase_value} // force purchase first
        />

        <Input
          type="number"
          label="Opening Book Value"
          name="opening_book_value"
          value={depreciationObject.opening_book_value}
          onChange={handleInputChange}
        />
        <Input
          type="number"
          label="Depreciation Months *"
          name="depreciation_months"
          value={depreciationObject.depreciation_months}
          onChange={handleInputChange}
        />
        <Input
          type="number"
          label="Periodic Depreciation Amount"
          name="periodic_depreciation_amount"
          value={depreciationObject.periodic_depreciation_amount}
          onChange={handleInputChange}
          disabled={true}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 mt-4">
        <AutoCompleteCommon
          typeOfAutocomplete={"Depreciation A/C *"}
          setMethod={setSelectedDepreciationAccount}
          selectedvalue={selectedDepreciationAccount}
        />

        <AutoCompleteCommon
          typeOfAutocomplete={"Accumulated Depreciation A/C *"}
          setMethod={setSelectedAccumulatedDepreciationAccount}
          selectedvalue={selectedAccumulatedDepreciationAccount}
        />

        <AutoCompleteCommon
          typeOfAutocomplete={"Asset A/C *"}
          setMethod={setSelectedAssetAccount}
          selectedvalue={selectedAssetAccount}
        />

        <Input
          type="date"
          label="Sold Date"
          name="sold_date"
          value={depreciationObject.sold_date}
          onChange={handleInputChange}
        />
        <Input
          type="number"
          label="Sold Value"
          name="sold_value"
          value={depreciationObject.sold_value}
          onChange={handleInputChange}
        />
      </div>
    </div>
  );
}

/* ---------- TAB 2 : DEPRECIATION & DISPOSAL ---------- */
function DepreciationTab(props) {
  const { depreciationList } = props;

  const [loading, setLoading] = useState(true);

  const formatDate = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">
        Depreciation & Disposal
      </h3>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {[
                "Sl No",
                "Description",
                "From Date",
                "To Date",
                "Dep Amount",
                "Period Caption",
              ].map((head) => (
                <th
                  key={head}
                  className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wide"
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">
            {depreciationList?.map((asset, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-2">{asset.PeriodNo}</td>
                <td className="px-4 py-2">{asset.Description}</td>
                <td className="px-4 py-2">{formatDate(asset.FromDate)}</td>
                <td className="px-4 py-2">{formatDate(asset.ToDate)}</td>
                <td className="px-4 py-2">{asset.DepAmount}</td>
                <td className="px-4 py-2">{asset.PeriodCaption}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
